import { Component, OnDestroy, OnInit } from "@angular/core";
import { DatePipe } from "@angular/common";
import { FormControl } from "@angular/forms";
import {
    BASE_CONSTANTS,
    ITCIApplicationAuditRecord,
    ITCIApplicationAuditRecords,
    ITCIEnvVars, strEnum,
    TCI_ENVIRONMENT_VAR_TYPE,
    TCITenantSelectorType
} from "@tci/common";
import { NgEnvVarsService } from "../../../ng-shared/ng-services/ng-env-vars.service";
import * as lodash from "lodash";
import { SelectItem } from "primeng/primeng";
import { Subscription } from "rxjs";
import { BaseAppDetailMenuComponentInput, BaseAppDetailMenuComponentOutput } from "../../ng-app-details-menu";
import { AbstractTCITenantComponent } from "../../../ng-tenant/tci-tenant-component";
import { BaseAppDetailHistoryService } from "./base-app-detail-history.service";

export const AUDIT_VIEW_STYLES = strEnum<string>([
    "raw",
    "table"
]);
export type AUDIT_VIEW_STYLES = keyof typeof AUDIT_VIEW_STYLES;

@Component({
    selector: TCITenantSelectorType.TCI_APP_DETAIL_HISTORY.toString(),
    templateUrl: "./base-app-detail-history.component.html",
    styleUrls: ["./base-app-detail-history.component.less"]
})
export class BaseAppDetailHistoryComponent
    <E, I extends BaseAppDetailMenuComponentInput, O extends BaseAppDetailMenuComponentOutput<E>>
    extends AbstractTCITenantComponent<E, I, O> implements OnInit, OnDestroy {

    private appId: string;
    private dateTimeFormat: string;
    private appHistoryData: ITCIApplicationAuditRecord[];
    private origAppHistoryData: ITCIApplicationAuditRecord[];
    private readonly historyViewStyles: SelectItem[] = [
        {
            label: "Table",
            value: AUDIT_VIEW_STYLES.table
        },
        {
            label: "Raw",
            value: AUDIT_VIEW_STYLES.raw
        }
    ];
    private historyViewStyle: AUDIT_VIEW_STYLES = AUDIT_VIEW_STYLES.table;
    private displayedOfRecords = 0;
    private totalOfRecords = 0;
    private subscriptions: Subscription[] = [];
    private searchFilterCtrl = new FormControl();

    constructor(
        private envVars: NgEnvVarsService,
        private appDetailHistoryService: BaseAppDetailHistoryService,
        private datePipe: DatePipe
    ) {
        super();

        // React on app refresh notification
        this.subscriptions.push(this.appDetailHistoryService.getAppRefreshNotifSubj()
            .filter(refr => refr.app.id === this.appId)
            .subscribe(() => this.getAppHistory())
        );
    }

    ngOnInit(): void {
        this
            .envVars
            .getEnvVar(
                TCI_ENVIRONMENT_VAR_TYPE.WEB_SERVER_DATE_TIME_FORMAT
            )
            .subscribe((envVars: ITCIEnvVars<any>) => {
                this.dateTimeFormat = envVars[TCI_ENVIRONMENT_VAR_TYPE.WEB_SERVER_DATE_TIME_FORMAT] as string || "dd MMM yyyy hh:mm:ss a Z";
            });
        this.appId = this.input.application.id;
        this.getAppHistory();

        this.subscriptions.push(this.searchFilterCtrl.valueChanges
            .debounceTime(300)
            .subscribe(() => this.findInHistory()));
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    private toggleRawView(event): void {
        if (event.value === AUDIT_VIEW_STYLES.raw) {
            this.historyViewStyle = AUDIT_VIEW_STYLES.raw;
        } else {
            this.historyViewStyle = AUDIT_VIEW_STYLES.table;
        }
    }

    private findInHistory(): void {
        this.appHistoryData = this.filterByKeyword();
    }

    private filterByKeyword(): ITCIApplicationAuditRecord[] {
        let entries: ITCIApplicationAuditRecord[] = [];

        if (this.searchFilterCtrl.value) {
            entries = this.origAppHistoryData.filter(item => {
                return this.shouldBeKept(item.actionSummary) ||
                    this.shouldBeKept(this.datePipe.transform(item.createdTime, `${BASE_CONSTANTS.TCI_DATE_TIME_FORMAT}`)) ||
                    this.shouldBeKept(item.userName) ||
                    this.shouldBeKept(item.action.toString()) ||
                    this.shouldBeKept(item.duration.toString());
            });
        } else {
            entries = this.origAppHistoryData;
        }
        this.displayedOfRecords = entries.length;
        return entries;
    }

    private shouldBeKept(sInput: string): boolean {
        return this.searchFilterCtrl.value ?
            sInput
                .toLowerCase()
                .search(lodash.escapeRegExp(this.searchFilterCtrl.value.toLowerCase())) > -1 :
            true;
    }

    private clearFilter(): void {
        this.searchFilterCtrl.setValue("");
        this.appHistoryData = this.origAppHistoryData;
    }

    private getAppHistory() {
        this.appDetailHistoryService.getApplicationHistory(this.appId).subscribe(
            (data: ITCIApplicationAuditRecords) => {
                this.origAppHistoryData = data.audits.slice();
                this.appHistoryData = data.audits.slice();
                this.displayedOfRecords = data.totalNum;
                this.totalOfRecords = data.totalNum;
            },
            error => {
                console.error(error);
            }
        );
    }
}
